import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import type { TooltipProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { ChartContainer, ChartTooltip, type ChartConfig } from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatDate } from '@/util/dateUtils';

import type { ChartSeries, ValueFormatter } from '../../hooks/useReportMetric';
import { type PeriodRange, padSeriesToPeriod } from '../../utils/padSeriesToPeriod';
import { MetricTooltipCard, MetricTooltipRow } from '../tooltip/MetricTooltipCard';

export const CHART_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
  'var(--chart-8)',
  'var(--chart-9)',
  'var(--chart-10)',
];

export const MAX_VISIBLE_SERIES = CHART_COLORS.length;

export const CHART_BOX_CLASS = 'aspect-4/1 min-h-40 w-full';

type SeriesColor = { series: ChartSeries; color: string };

export type ChartRow = { date: string; [seriesKey: string]: string | number | undefined };

/**
 * Merge per-series points into a single row-per-date matrix Recharts consumes:
 *   [{ date, [seriesKey1]: v, [seriesKey2]: v }, ...]
 */
const buildChartData = (visible: SeriesColor[]): ChartRow[] => {
  const dateToRow = new Map<string, ChartRow>();
  visible.forEach(({ series }) => {
    series.data.forEach((point) => {
      const row: ChartRow = dateToRow.get(point.date) ?? { date: point.date };
      row[series.key] = point.value;
      dateToRow.set(point.date, row);
    });
  });
  return Array.from(dateToRow.values()).sort((a, b) => a.date.localeCompare(b.date));
};

const buildChartConfig = (visible: SeriesColor[]): ChartConfig =>
  visible.reduce<ChartConfig>((acc, { series, color }) => {
    acc[series.key] = { label: series.label, color };
    return acc;
  }, {});

const SeriesTooltip = ({
  active,
  payload,
  label,
  visible,
  valueFormatter,
}: TooltipProps<ValueType, NameType> & {
  visible: SeriesColor[];
  valueFormatter: ValueFormatter;
}) => {
  if (!active || !payload?.length || typeof label !== 'string') {
    return null;
  }
  const formattedDate = formatDate(label as string, 'MMM d, yyyy');
  const byKey = new Map(payload.map((p) => [p.dataKey as string, p.value as number]));
  return (
    <MetricTooltipCard title={formattedDate}>
      {visible.map(({ series, color }) => {
        const value = byKey.get(series.key);
        if (value == null) {
          return null;
        }
        return (
          <MetricTooltipRow
            key={series.key}
            label={series.label}
            value={valueFormatter(value)}
            color={color}
          />
        );
      })}
    </MetricTooltipCard>
  );
};

const defaultValueFormatter: ValueFormatter = (value) => value.toLocaleString();

const TICK_CHAR_PX = 7;
const TICK_MARGIN_PX = 8;
const TICK_SAFETY_PX = 4;
const TICK_COUNT = 5;

const digitCount = (value: number): number =>
  value === 0 ? 1 : Math.floor(Math.log10(Math.abs(value))) + 1;

const exact = (value: number): number => Number(value.toPrecision(12));

const tickStep = (max: number): number => {
  const rough = max / (TICK_COUNT - 1);
  if (rough <= 0) {
    return 0;
  }
  const magnitude = 10 ** digitCount(rough);
  const scale = digitCount(rough) === 1 ? 0.1 : 0.05;
  return exact(Math.ceil(Number((rough / magnitude / scale).toFixed(6))) * scale * magnitude);
};

type TickOptions = { integerOnly?: boolean };

const getYAxisTicks = (max: number, { integerOnly = false }: TickOptions): number[] => {
  const step = tickStep(max);
  if (step <= 0) {
    return [0];
  }
  // A count has no fractions, so a step below 1 becomes one tick per whole number.
  if (integerOnly && step < 1) {
    return Array.from({ length: Math.ceil(max) + 1 }, (_, i) => i);
  }
  const count = Math.max(TICK_COUNT, Math.ceil(max / step) + 1);
  return Array.from({ length: count }, (_, i) => exact(i * step));
};

export const getYAxisTicksFor = (series: ChartSeries[], options: TickOptions = {}): number[] => {
  let max = 0;
  for (const { data } of series) {
    for (const { value } of data) {
      if (Number.isFinite(value) && value > max) {
        max = value;
      }
    }
  }
  return getYAxisTicks(max, options);
};

export const toStackTotals = (rows: ChartRow[], keys: string[]): ChartSeries[] => [
  {
    key: 'total',
    label: 'total',
    data: rows.map((row) => ({
      date: row.date,
      value: keys.reduce((sum, key) => sum + (Number(row[key]) || 0), 0),
    })),
  },
];

export const getYAxisWidth = (
  series: ChartSeries[],
  format: ValueFormatter,
  options: TickOptions = {}
): number => {
  const longest = Math.max(...getYAxisTicksFor(series, options).map((v) => format(v).length));
  return longest * TICK_CHAR_PX + TICK_MARGIN_PX + TICK_SAFETY_PX;
};

const pickVisibleSeries = (
  series: ChartSeries[],
  colorByKey: Map<string, string> | undefined
): SeriesColor[] => {
  if (colorByKey && colorByKey.size > 0) {
    const seriesByKey = new Map(series.map((s) => [s.key, s]));
    return Array.from(colorByKey.entries()).flatMap(([key, color]) => {
      const s = seriesByKey.get(key);
      return s ? [{ series: s, color }] : [];
    });
  }
  return series.slice(0, MAX_VISIBLE_SERIES).map((s, i) => ({ series: s, color: CHART_COLORS[i] }));
};

const padToPeriod = (visible: SeriesColor[], period: PeriodRange): SeriesColor[] =>
  visible.map(({ series, color }) => ({
    color,
    series: {
      ...series,
      data: padSeriesToPeriod(series.data, period, 'zero').map((p) => ({
        date: p.date,
        value: p.value ?? 0,
      })),
    },
  }));

type DotProps = { cx?: number; cy?: number; index?: number; payload?: { date?: string } };

// A dot only for a point with no neighbor: a lone point draws no line, so it
// would be invisible at rest.
const renderIsolatedDot = (
  { cx, cy, index, payload }: DotProps,
  rows: ChartRow[],
  key: string,
  color: string
) => {
  if (cx == null || cy == null || index == null) {
    return <g key={`${key}-${payload?.date ?? index ?? 'empty'}`} />;
  }
  if (rows[index - 1]?.[key] != null || rows[index + 1]?.[key] != null) {
    return <g key={`${key}-${index}`} />;
  }
  return <circle key={`${key}-${index}`} cx={cx} cy={cy} r={3} fill={color} />;
};

// Recharts 2 finds its children by element type, so these return elements
// instead of being components.
const renderLines = (visible: SeriesColor[], rows: ChartRow[]) =>
  visible.map(({ series, color }) => (
    <Line
      key={series.key}
      type="monotone"
      dataKey={series.key}
      stroke={color}
      strokeWidth={2}
      dot={(props: DotProps) => renderIsolatedDot(props, rows, series.key, color)}
      activeDot={{ r: 5, fill: color, stroke: '#fff', strokeWidth: 2 }}
    />
  ));

const renderStackedBars = (visible: SeriesColor[]) =>
  visible.map(({ series, color }) => (
    <Bar key={series.key} dataKey={series.key} stackId="stack" fill={color} maxBarSize={48} />
  ));

type ReportMetricChartProps = {
  series: ChartSeries[];
  isLoading: boolean;
  isError: boolean;
  /**
   * When provided, only series whose `key` is in the map are rendered, in the
   * map's iteration order, using the map's color. Lets the parent keep chart
   * and table coloring in sync. When omitted, the chart falls back to picking
   * the first `MAX_VISIBLE_SERIES` series in input order.
   */
  colorByKey?: Map<string, string>;
  /** Formats Y-axis ticks and tooltip values. Defaults to locale-string. */
  valueFormatter?: ValueFormatter;
  /** `bar` stacks the series per day, for counts of discrete values, on a whole-number axis. */
  chartType?: 'line' | 'bar';
  /** Bar mode only: every day of this period gets a slot on the x-axis. */
  period?: PeriodRange;
};

export const ReportMetricChart = ({
  series,
  isLoading,
  isError,
  colorByKey,
  valueFormatter = defaultValueFormatter,
  chartType = 'line',
  period,
}: ReportMetricChartProps) => {
  const { t } = useTranslation();

  const visible = useMemo(() => pickVisibleSeries(series, colorByKey), [series, colorByKey]);
  const chartConfig = useMemo(() => buildChartConfig(visible), [visible]);

  const isBar = chartType === 'bar';
  const chartData = buildChartData(isBar && period ? padToPeriod(visible, period) : visible);
  const scaleSeries = isBar
    ? toStackTotals(
        chartData,
        visible.map((v) => v.series.key)
      )
    : visible.map((v) => v.series);
  const tickOptions = { integerOnly: isBar };
  const yTicks = getYAxisTicksFor(scaleSeries, tickOptions);
  const yTop = yTicks[yTicks.length - 1];
  const Chart = isBar ? BarChart : LineChart;

  if (isLoading) {
    return <Skeleton className={cn(CHART_BOX_CLASS, 'rounded-lg')} />;
  }
  if (isError) {
    return (
      <div className={cn('flex items-center justify-center', CHART_BOX_CLASS)}>
        <p className="text-sm leading-5 text-muted-foreground">
          {t('analytics.reports_detail_metric_query_error')}
        </p>
      </div>
    );
  }

  return (
    <ChartContainer config={chartConfig} className={CHART_BOX_CLASS}>
      <Chart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value) => formatDate(value, 'MMM d')}
          interval="preserveStartEnd"
          minTickGap={40}
        />
        <YAxis
          ticks={yTop > 0 ? yTicks : undefined}
          domain={[0, yTop > 0 ? yTop : 'auto']}
          tickCount={TICK_COUNT}
          tickLine={false}
          axisLine={false}
          tickSize={0}
          tickMargin={TICK_MARGIN_PX}
          width={getYAxisWidth(scaleSeries, valueFormatter, tickOptions)}
          tickFormatter={(value: number) => valueFormatter(value)}
        />
        <ChartTooltip
          content={<SeriesTooltip visible={visible} valueFormatter={valueFormatter} />}
        />
        {isBar ? renderStackedBars(visible) : renderLines(visible, chartData)}
      </Chart>
    </ChartContainer>
  );
};
